/**
 * Chunk prefetching.
 *
 * Code splitting takes bytes off the first load, but it also means the first
 * navigation to a split route pays a fetch that did not exist before. On a
 * fast connection that surfaces as a spinner where content used to appear
 * instantly -- the regression that gives code splitting its bad name.
 *
 * So every split point is warmed ahead of use: on idle once the first screen
 * has painted, for the routes a session is likely to reach, and on hover or
 * focus for the ones it might. By the time a click lands the module is
 * usually already resolved and the Suspense fallback never renders.
 */

// Importers already started. Keyed rather than holding the promise, because
// the module registry does the real caching -- this only stops us calling an
// importer repeatedly on every hover.
const started = new Set()

/**
 * Start one importer, at most once.
 *
 * A rejected import is forgotten rather than remembered as done: offline, or
 * a deploy mid-session invalidating the content-hashed filename, would
 * otherwise poison the entry and leave the real navigation with nothing to
 * retry. Failures are swallowed -- a prefetch that does not land is a
 * non-event, and the navigation itself will surface any genuine problem.
 */
export function prefetch(key, importer) {
  if (started.has(key)) return
  started.add(key)
  Promise.resolve().then(importer).catch(() => { started.delete(key) })
}

/**
 * Prefetching is speculation, so never spend a connection the user is trying
 * to conserve on it. `navigator.connection` is absent on Safari/iOS, where
 * the check falls through to enabled -- the right default, since the
 * alternative is switching prefetching off for every iOS visitor, and iOS is
 * a first-class target here.
 */
// Anything below 4g is left alone. Speculation competes with the real data
// fetches for the same connection, and the field operator on a weak link is
// both the person who gains most from the smaller first load and the person
// who can least afford 300 KB of guessing behind it. Absent effectiveType
// (Safari/iOS) falls through to enabled.
const TOO_SLOW = new Set(['slow-2g', '2g', '3g'])

function shouldPrefetch() {
  const c = navigator.connection
  if (!c) return true
  if (c.saveData) return false
  return !TOO_SLOW.has(c.effectiveType)
}

// Safari still ships no requestIdleCallback. The timer fallback is a coarse
// stand-in: it cannot wait for genuine idle, so it is set late enough to land
// after first paint and the initial /me and data fetches rather than
// competing with them for the connection.
const onIdle = typeof requestIdleCallback === 'function'
  ? (cb) => requestIdleCallback(cb, { timeout: 3000 })
  : (cb) => setTimeout(cb, 1500)

/**
 * Warm a list of [key, importer] pairs once the browser is idle.
 * Returns a cancel function, so a component unmounting before the callback
 * fires does not queue work for a screen nobody is on any more.
 */
export function prefetchOnIdle(entries) {
  if (!shouldPrefetch()) return () => {}
  let cancelled = false
  onIdle(() => {
    if (cancelled) return
    for (const [key, importer] of entries) prefetch(key, importer)
  })
  return () => { cancelled = true }
}

/**
 * Props for a link or control leading somewhere split.
 *
 * Hover-to-click is typically 100-300ms, which covers most of a chunk fetch
 * off a warm edge. `onFocus` covers keyboard navigation, which never fires
 * hover; `onTouchStart` covers touch, which fires no hover either and where
 * the gap to the click is smaller but still real.
 */
export function intentHandlers(key, importer) {
  const go = () => prefetch(key, importer)
  return { onMouseEnter: go, onFocus: go, onTouchStart: go }
}
